<template>
  <div class="page-container">
    <el-card v-loading="loading">
      <div slot="header" class="page-header">
        <span class="page-title">合伙人信息</span>
        <div class="page-actions">
          <el-button size="small" @click="$router.back()">返回列表</el-button>
          <el-button
            v-permission="['partner:update']"
            type="primary"
            size="small"
            :loading="saving"
            @click="handleSave"
          >保存</el-button>
        </div>
      </div>

      <template v-if="detail">
        <el-form ref="form" :model="form" :rules="rules" label-width="100px" class="partner-form">

          <!-- 手机号（只读） -->
          <el-form-item label="手机号">
            <el-input :value="detail.phone" readonly class="w-md" />
            <span class="form-tip ml">手机号不可修改</span>
          </el-form-item>

          <!-- 姓名 -->
          <el-form-item label="姓名" prop="name">
            <el-input v-model="form.name" placeholder="请输入姓名" class="w-md" />
          </el-form-item>


          <!-- 性别 -->
          <el-form-item label="性别">
            <el-radio-group v-model="form.gender">
              <el-radio :label="1">男</el-radio>
              <el-radio :label="2">女</el-radio>
            </el-radio-group>
          </el-form-item>

          <!-- 录入年龄 -->
          <el-form-item label="录入年龄">
            <el-input-number v-model="form.age_at_entry" :min="1" :max="120" style="width: 130px" />
            <span class="form-tip ml">录入时的年龄，当前实际年龄：{{ detail.current_age || '—' }} 岁</span>
          </el-form-item>

          <!-- 住址 -->
          <el-form-item label="住址">
            <el-input v-model="form.address" placeholder="请输入住址（可选）" class="w-lg" />
          </el-form-item>

          <!-- 头像 -->
          <el-form-item label="头像" prop="avatar">
            <div class="image-list">
              <div v-if="form.avatar" class="image-box">
                <el-image :src="form.avatar" fit="cover" class="image-inner" :preview-src-list="[form.avatar]" />
                <div class="image-mask">
                  <i class="el-icon-delete" @click="clearImage('avatar')" />
                </div>
              </div>
              <el-upload
                v-if="!form.avatar"
                action="#"
                accept="image/jpeg,image/png,image/webp"
                :show-file-list="false"
                :http-request="({ file }) => handleSingleUpload(file, 'avatar')"
              >
                <div class="image-add-box" :class="{ 'is-error': imageErrors.avatar }">
                  <i :class="uploading.avatar ? 'el-icon-loading' : 'el-icon-plus'" />
                </div>
              </el-upload>
            </div>
            <div v-if="imageErrors.avatar" class="image-error-tip">请上传头像</div>
            <div v-else class="form-tip">支持 JPG/PNG/WebP，建议正方形</div>
          </el-form-item>

          <!-- 身份证正面 -->
          <el-form-item label="身份证正面" prop="id_card_front">
            <div class="image-list">
              <div v-if="form.id_card_front" class="image-box id-card-box">
                <el-image :src="form.id_card_front" fit="cover" class="image-inner" :preview-src-list="idCardPreviewList" />
                <div class="image-mask">
                  <i class="el-icon-delete" @click="clearImage('id_card_front')" />
                </div>
              </div>
              <el-upload
                v-if="!form.id_card_front"
                action="#"
                accept="image/jpeg,image/png,image/webp"
                :show-file-list="false"
                :http-request="({ file }) => handleSingleUpload(file, 'id_card_front')"
              >
                <div class="image-add-box id-card-box" :class="{ 'is-error': imageErrors.id_card_front }">
                  <i :class="uploading.id_card_front ? 'el-icon-loading' : 'el-icon-plus'" />
                </div>
              </el-upload>
            </div>
            <div v-if="imageErrors.id_card_front" class="image-error-tip">请上传身份证正面</div>
          </el-form-item>

          <!-- 身份证反面 -->
          <el-form-item label="身份证反面" prop="id_card_back">
            <div class="image-list">
              <div v-if="form.id_card_back" class="image-box id-card-box">
                <el-image :src="form.id_card_back" fit="cover" class="image-inner" :preview-src-list="idCardPreviewList" />
                <div class="image-mask">
                  <i class="el-icon-delete" @click="clearImage('id_card_back')" />
                </div>
              </div>
              <el-upload
                v-if="!form.id_card_back"
                action="#"
                accept="image/jpeg,image/png,image/webp"
                :show-file-list="false"
                :http-request="({ file }) => handleSingleUpload(file, 'id_card_back')"
              >
                <div class="image-add-box id-card-box" :class="{ 'is-error': imageErrors.id_card_back }">
                  <i :class="uploading.id_card_back ? 'el-icon-loading' : 'el-icon-plus'" />
                </div>
              </el-upload>
            </div>
            <div v-if="imageErrors.id_card_back" class="image-error-tip">请上传身份证反面</div>
          </el-form-item>

          <!-- 银行卡号 -->
          <el-form-item label="银行卡号" prop="bank_card_no">
            <el-input
              v-model="form.bank_card_no"
              placeholder="请输入银行卡号"
              class="w-md"
              maxlength="25"
            />
          </el-form-item>

          <!-- 开户行 -->
          <el-form-item label="开户行" prop="bank_name">
            <el-input
              v-model="form.bank_name"
              placeholder="请输入开户行"
              class="w-md"
            />
          </el-form-item>

          <!-- 备注 -->
          <el-form-item label="备注">
            <el-input
              v-model="form.remark"
              type="textarea"
              :rows="4"
              placeholder="请输入备注（可选）"
              class="w-lg"
            />
          </el-form-item>

          <!-- 审核状态 -->
          <el-form-item label="审核状态">
            <el-radio-group v-model="form.review_status">
              <el-radio :label="1">通过</el-radio>
              <el-radio :label="2">不通过</el-radio>
            </el-radio-group>
            <span v-if="detail.reviewed_by" class="form-tip ml">
              审核人：{{ detail.reviewed_by }}
              <template v-if="detail.reviewed_at">（{{ detail.reviewed_at }}）</template>
            </span>
          </el-form-item>

          <!-- 底部按钮 -->
          <el-form-item>
            <el-button size="small" @click="$router.back()">返回列表</el-button>
            <el-button
              v-permission="['partner:update']"
              type="primary"
              size="small"
              :loading="saving"
              @click="handleSave"
            >保存</el-button>
          </el-form-item>

        </el-form>
      </template>
    </el-card>
  </div>
</template>

<script>
import { getPartnerDetail, updatePartner, uploadPartnerImage } from '@/api/partner'

export default {
  name: 'PartnerDetail',
  data() {
    return {
      loading: false,
      saving: false,
      uploading: {
        avatar: false,
        id_card_front: false,
        id_card_back: false
      },
      imageErrors: {
        avatar: false,
        id_card_front: false,
        id_card_back: false
      },
      detail: null,
      form: {
        name: '',
        gender: 1,
        age_at_entry: null,
        avatar: '',
        address: '',
        id_card_front: '',
        id_card_back: '',
        bank_card_no: '',
        bank_name: '',
        remark: '',
        review_status: 0,
        status: 1
      },
      rules: {
        name:        [{ required: true, message: '请输入姓名', trigger: 'blur' }],
        bank_card_no:[{ required: true, message: '请输入银行卡号', trigger: 'blur' }],
        bank_name:   [{ required: true, message: '请输入开户行', trigger: 'blur' }]
      }
    }
  },
  computed: {
    idCardPreviewList() {
      return [this.form.id_card_front, this.form.id_card_back].filter(Boolean)
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler(to) {
        if (to.path !== '/partner/detail') return
        if (to.query.id) this.fetchDetail(to.query.id)
      }
    }
  },
  methods: {
    async fetchDetail(id) {
      this.loading = true
      try {
        const res = await getPartnerDetail({ id: Number(id) })
        const d = res.data
        this.detail = d
        this.form = {
          name:          d.name || '',
          gender:        d.gender || 1,
          age_at_entry:  d.age_at_entry || null,
          avatar:        d.avatar || '',
          address:       d.address || '',
          id_card_front: d.id_card_front || '',
          id_card_back:  d.id_card_back || '',
          bank_card_no:  d.bank_card_no || '',
          bank_name:     d.bank_name || '',
          remark:        d.remark || '',
          review_status: d.review_status ?? 0,
          status:        d.status ?? 1
        }
      } finally {
        this.loading = false
      }
    },

    async handleSingleUpload(file, field) {
      this.uploading[field] = true
      try {
        const res = await uploadPartnerImage(file)
        this.form[field] = res.data.url
        this.imageErrors[field] = false
        this.$message.success('上传成功')
      } catch (e) {
        this.$message.error(e.message || '上传失败')
      } finally {
        this.uploading[field] = false
      }
    },

    clearImage(field) {
      this.form[field] = ''
      this.imageErrors[field] = true
    },

    validateImages() {
      const imageFields = ['avatar', 'id_card_front', 'id_card_back']
      let valid = true
      imageFields.forEach(f => {
        const empty = !this.form[f]
        this.imageErrors[f] = empty
        if (empty) valid = false
      })
      return valid
    },

    handleSave() {
      const imagesValid = this.validateImages()
      this.$refs.form.validate(async formValid => {
        if (!formValid || !imagesValid) {
          this.$message.warning('请填写所有必填项')
          return
        }
        this.saving = true
        try {
          const payload = {
            id:            this.detail.id,
            name:          this.form.name,
            gender:        this.form.gender,
            age_at_entry:  this.form.age_at_entry || undefined,
            avatar:        this.form.avatar,
            address:       this.form.address || null,
            id_card_front: this.form.id_card_front,
            id_card_back:  this.form.id_card_back,
            bank_card_no:  this.form.bank_card_no,
            bank_name:     this.form.bank_name,
            remark:        this.form.remark,
            review_status: this.form.review_status,
            status:        this.form.status
          }
          const res = await updatePartner(payload)
          this.detail = res.data
          this.$message.success('保存成功')
        } finally {
          this.saving = false
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  .page-title { font-size: 15px; font-weight: 600; color: #303133; }
  .page-actions { display: flex; gap: 8px; }
}

.partner-form {
  max-width: 760px;
  padding-top: 8px;
  .w-md { width: 340px; }
  .w-lg { width: 560px; }
}

.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  display: block;
  &.ml { display: inline; margin-top: 0; margin-left: 8px; }
}

/* 图片上传 */
.image-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: flex-start;
}

.image-box {
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #e4e7ed;
  flex-shrink: 0;

  &.id-card-box { width: 180px; height: 120px; }

  .image-inner { width: 100%; height: 100%; display: block; }

  .image-mask {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s;
    i {
      font-size: 20px;
      color: #fff;
      cursor: pointer;
      &.el-icon-delete:hover { color: #f56c6c; }
    }
  }
  &:hover .image-mask { opacity: 1; }
}

.image-add-box {
  width: 120px;
  height: 120px;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #fafafa;
  transition: border-color 0.2s, background 0.2s;

  &.id-card-box { width: 180px; height: 120px; }

  &.is-error {
    border-color: #f56c6c;
    background: #fff5f5;
  }

  i {
    font-size: 28px;
    color: #c0c4cc;
    transition: color 0.2s;
  }
  &:hover {
    border-color: #409eff;
    background: #f0f7ff;
    i { color: #409eff; }
  }
}

.image-error-tip {
  font-size: 12px;
  color: #f56c6c;
  margin-top: 4px;
  line-height: 1;
}
</style>
